<template>
  <div class="prompt-settings-container">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h1>Gestión de Prompts de IA</h1>
        <p class="text-muted">
          Prompts usados por el asistente de soporte según la petición del usuario.
        </p>
      </div>
      <div>
        <button
          @click="$router.push('/gotrading/ai-model-context')"
          class="btn btn-outline-primary"
        >
          <i class="fas fa-robot me-1"></i>
          Configurar Contexto IA
        </button>
      </div>
    </div>


    <div class="alert alert-info">
      <strong>Estado:</strong>
      Modelos disponibles: {{ availableModels.length }} | Prompts cargados:
      {{ prompts.length }}
    </div>

    <div class="row">
      <!-- Lista de prompts -->
      <div class="col-md-8">
        <div class="card">
          <div
            class="card-header d-flex justify-content-between align-items-center"
          >
            <h5 class="mb-0">Prompts Disponibles</h5>
            <button @click="openCreateModal" class="btn btn-primary btn-sm">
              <i class="fas fa-plus me-2"></i>
              Nuevo Prompt
            </button>
          </div>
          <div class="card-body">
            <div v-if="prompts.length === 0" class="text-center py-4">
              <i class="fas fa-robot fa-3x text-muted mb-3"></i>
              <p class="text-muted">No hay prompts configurados</p>
              <button @click="openCreateModal" class="btn btn-primary">
                Crear primer prompt
              </button>
            </div>

            <div v-else class="prompts-list">
              <section
                v-for="group in promptsGroupedByCategory"
                :key="group.categoryName"
                class="prompt-category-group"
              >
                <h6 class="prompt-category-heading">
                  <span class="badge bg-primary">{{ group.categoryName }}</span>
                  <span class="text-muted small fw-normal">
                    {{ group.prompts.length }}
                    {{
                      group.prompts.length === 1 ? "prompt" : "prompts"
                    }}
                  </span>
                </h6>
                <div
                  v-for="prompt in group.prompts"
                  :key="prompt._id"
                  class="prompt-item card mb-2"
                >
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <h6 class="card-title">{{ prompt.name }}</h6>
                      <p class="card-text text-muted">
                        {{ prompt.description }}
                      </p>
                      <div class="prompt-content">
                        <strong>Prompt:</strong>
                        <p class="mt-2">{{ prompt.content }}</p>
                      </div>
                      <div class="prompt-meta mt-2">
                        <span
                          v-if="prompt.isDefault"
                          class="badge bg-warning text-dark me-2"
                        >
                          Por defecto
                        </span>
                        <span class="badge bg-secondary">{{
                          promptModelLabel(prompt)
                        }}</span>
                      </div>
                    </div>
                    <div class="btn-group">
                      <button
                        @click="editPrompt(prompt)"
                        class="btn btn-outline-primary btn-sm"
                        title="Editar"
                      >
                        <i class="fas fa-edit"></i>
                      </button>
                      <button
                        @click="deletePrompt(prompt)"
                        class="btn btn-outline-danger btn-sm"
                        title="Eliminar"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <!-- Información y ayuda -->
      <div class="col-md-4">
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0">
              <i class="fas fa-info-circle me-2"></i>
              Información
            </h6>
          </div>
          <div class="card-body">
            <h6>¿Qué son los prompts?</h6>
            <p class="small text-muted">
              Los prompts son instrucciones que le das a la IA para analizar el
              contenido. Puedes crear prompts personalizados para diferentes
              tipos de análisis.
            </p>

            <h6 class="mt-3">Variables disponibles:</h6>
            <ul class="small text-muted">
              <li><code>{modelName}</code> - Nombre del modelo</li>
              <li><code>{modelLabel}</code> - Etiqueta del modelo</li>
              <li><code>{itemId}</code> - ID del registro</li>
              <li><code>{idModel}</code> - ID del modelo</li>
            </ul>

            <h6 class="mt-3">Categorías</h6>
            <p class="small text-muted mb-2">
              Creá, editá o eliminá categorías para clasificar tus prompts.
            </p>
            <div v-if="categories.length === 0" class="small text-muted mb-2">
              No hay categorías. Creá la primera para poder guardar prompts.
            </div>
            <ul v-else class="list-unstyled small mb-2 prompt-category-list">
              <li
                v-for="cat in categories"
                :key="cat._id"
                class="d-flex justify-content-between align-items-center mb-1"
              >
                <span>{{ cat.name }}</span>
                <span class="btn-group btn-group-sm">
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm py-0 px-1"
                    title="Editar"
                    @click="editCategory(cat)"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm py-0 px-1"
                    title="Eliminar"
                    @click="deleteCategory(cat)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </span>
              </li>
            </ul>
            <div v-if="showCategoryForm" class="mb-2">
              <input
                v-model="categoryForm.name"
                type="text"
                class="form-control form-control-sm mb-1"
                maxlength="120"
                placeholder="Nombre de categoría"
              />
              <div class="d-flex gap-1">
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  :disabled="!categoryForm.name.trim()"
                  @click="saveCategory"
                >
                  {{ editingCategory ? "Actualizar" : "Crear" }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  @click="cancelCategoryForm"
                >
                  Cancelar
                </button>
              </div>
            </div>
            <button
              v-else
              type="button"
              class="btn btn-sm btn-outline-primary w-100 mb-2"
              @click="openNewCategoryForm"
            >
              <i class="fas fa-plus me-1"></i>
              Nueva categoría
            </button>

            <hr class="my-3" />

            <h6>Contexto de IA por Modelo</h6>
            <p class="small text-muted">
              Cada modelo puede tener un contexto específico que se envía
              automáticamente a la IA junto con cada prompt. Esto ayuda a la IA
              a entender mejor el propósito del modelo.
            </p>
            <button
              @click="$router.push('/gotrading/ai-model-context')"
              class="btn btn-sm btn-outline-primary"
            >
              <i class="fas fa-robot me-1"></i>
              Configurar Contextos
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar prompt -->
    <div
      v-if="showCreateModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-edit me-2"></i>
              {{ editingPrompt ? "Editar Prompt" : "Nuevo Prompt" }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="savePrompt">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label for="promptName" class="form-label">
                    <strong>Nombre del Prompt *</strong>
                  </label>
                  <input
                    id="promptName"
                    v-model="promptForm.name"
                    type="text"
                    class="form-control"
                    required
                    placeholder="Ej: Análisis ejecutivo"
                  />
                </div>

                <div class="col-md-6 mb-3">
                  <label for="promptCategory" class="form-label">
                    <strong>Categoría *</strong>
                  </label>
                  <select
                    id="promptCategory"
                    v-model="promptForm.category"
                    class="form-select"
                    required
                    :disabled="categories.length === 0"
                  >
                    <option value="">
                      {{
                        categories.length
                          ? "Selecciona una categoría"
                          : "Creá una categoría primero"
                      }}
                    </option>
                    <option
                      v-for="cat in categories"
                      :key="cat._id"
                      :value="cat.name"
                    >
                      {{ cat.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="mb-3">
                <label for="promptDescription" class="form-label">
                  <strong>Descripción</strong>
                </label>
                <input
                  id="promptDescription"
                  v-model="promptForm.description"
                  type="text"
                  class="form-control"
                  placeholder="Breve descripción del prompt"
                />
              </div>

              <div class="mb-3">
                <label for="promptContent" class="form-label">
                  <strong>Contenido del Prompt *</strong>
                </label>

                <!-- Selector de campos del modelo -->
                <div class="mb-2" v-if="selectedModelFields.length > 0">
                  <label class="form-label">
                    <strong>Campos disponibles del modelo:</strong>
                  </label>
                  <div class="field-selector">
                    <div
                      v-for="field in selectedModelFields"
                      :key="field.key"
                      class="field-item"
                      :class="{ 'field-group': field.type === 'group' }"
                      draggable="true"
                      @dragstart="onDragStart($event, field)"
                      @click="insertField(field)"
                    >
                      <i
                        :class="
                          field.type === 'group'
                            ? 'fas fa-layer-group me-1'
                            : 'fas fa-tag me-1'
                        "
                      ></i>
                      {{ field.label || field.key }}
                      <small class="text-muted">
                        {{
                          field.type === "group"
                            ? "(Grupo)"
                            : `([${field.key}])`
                        }}
                      </small>
                    </div>
                  </div>
                  <small class="form-text text-muted">
                    Haz clic en un campo para insertarlo o arrastra y suelta en
                    el editor
                  </small>
                </div>

                <textarea
                  id="promptContent"
                  v-model="promptForm.content"
                  class="form-control"
                  rows="8"
                  required
                  placeholder="Escribe aquí el prompt que quieres usar para el análisis..."
                  @drop="onDrop"
                  @dragover.prevent
                ></textarea>
                <small class="form-text text-muted">
                  <strong>Variables disponibles:</strong> {modelName},
                  {modelLabel}, {itemId}, {idModel}<br />
                  <strong>Campos del modelo:</strong> Usa [nombreCampo] para
                  insertar valores específicos<br />
                  <strong>Campos de grupo:</strong> Los campos con icono de
                  capas se mostrarán como JSON con toda su información
                </small>
              </div>

              <div class="mb-3">
                  <label for="promptModelName" class="form-label">
                    <strong>Modelo de contenido</strong>
                  </label>
                  <select
                    id="promptModelName"
                    v-model="promptForm.modelName"
                    class="form-select"
                  >
                    <option value="">
                      Genérico (todos los modelos)
                    </option>
                    <option
                      v-for="model in availableModels"
                      :key="model.modelName"
                      :value="model.modelName"
                    >
                      {{ model.label || model.modelName }}
                    </option>
                  </select>
                  <small class="form-text text-muted">
                    Opcional. Sin modelo, el prompt es genérico y aplica en
                    cualquier contexto (soporte, listados, etc.).
                  </small>
              </div>

              <div class="mb-3">
                <div class="form-check">
                  <input
                    id="promptIsDefault"
                    v-model="promptForm.isDefault"
                    type="checkbox"
                    class="form-check-input"
                  />
                  <label class="form-check-label" for="promptIsDefault">
                    Marcar como prompt por defecto
                  </label>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="savePrompt"
              :disabled="!canSavePrompt"
            >
              <i class="fas fa-save me-2"></i>
              {{ editingPrompt ? "Actualizar" : "Guardar" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay para el modal -->
    <div
      v-if="showCreateModal"
      class="modal-backdrop fade show"
      style="
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1040;
      "
    ></div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
export default {
  name: "PromptSettings",
  data() {
    return {
      prompts: [],
      categories: [],
      showCreateModal: false,
      editingPrompt: null,
      showCategoryForm: false,
      editingCategory: null,
      categoryForm: { name: "" },
      promptForm: {
        name: "",
        description: "",
        content: "",
        category: "",
        modelName: "",
        isDefault: false,
      },
      availableModels: [],
      selectedModelFields: [],
      loading: false,
    };
  },
  computed: {
    canSavePrompt() {
      return (
        this.promptForm.name.trim() &&
        this.promptForm.content.trim() &&
        this.promptForm.category &&
        this.categories.length > 0
      );
    },
    promptsGroupedByCategory() {
      const byCat = new Map();
      for (const p of this.prompts) {
        const name =
          p.category != null && String(p.category).trim()
            ? String(p.category).trim()
            : "Sin categoría";
        if (!byCat.has(name)) byCat.set(name, []);
        byCat.get(name).push(p);
      }

      const groups = [];
      const seen = new Set();

      for (const cat of this.categories) {
        const list = byCat.get(cat.name);
        if (list && list.length) {
          groups.push({
            categoryName: cat.name,
            prompts: [...list].sort((a, b) =>
              String(a.name || "").localeCompare(String(b.name || ""), "es", {
                sensitivity: "base",
              })
            ),
          });
          seen.add(cat.name);
        }
      }

      const orphanNames = [...byCat.keys()]
        .filter((n) => !seen.has(n))
        .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));

      for (const name of orphanNames) {
        const list = byCat.get(name);
        groups.push({
          categoryName: name,
          prompts: [...list].sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), "es", {
              sensitivity: "base",
            })
          ),
        });
      }

      return groups;
    },
  },
  watch: {
    "promptForm.modelName": {
      handler(newModelName) {
        if (newModelName) {
          this.loadModelFields(newModelName);
        } else {
          this.selectedModelFields = [];
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    await Promise.all([
      this.loadPrompts(),
      this.loadCategories(),
      this.loadAvailableModels(),
    ]);
  },
  methods: {
    openCreateModal() {
      console.log("Abriendo modal de creación...");
      this.showCreateModal = true;
      this.editingPrompt = null;
      this.resetForm();
      console.log("Modal abierto:", this.showCreateModal);
    },

    async loadCategories() {
      try {
        const response = await wencesApi.get("/api/gotrading/ai/prompts/categories");
        if (response.data.success) {
          this.categories = response.data.categories || [];
          if (
            !this.promptForm.category &&
            this.categories.length > 0 &&
            !this.showCreateModal
          ) {
            this.promptForm.category = this.categories[0].name;
          }
        }
      } catch (error) {
        console.error("Error loading categories:", error);
        Swal.fire({
          title: "Error",
          text: "Error al cargar las categorías",
          icon: "error",
        });
      }
    },

    openNewCategoryForm() {
      this.editingCategory = null;
      this.categoryForm = { name: "" };
      this.showCategoryForm = true;
    },

    editCategory(cat) {
      this.editingCategory = cat;
      this.categoryForm = { name: cat.name };
      this.showCategoryForm = true;
    },

    cancelCategoryForm() {
      this.showCategoryForm = false;
      this.editingCategory = null;
      this.categoryForm = { name: "" };
    },

    async saveCategory() {
      const name = String(this.categoryForm.name || "").trim();
      if (!name) return;

      try {
        if (this.editingCategory) {
          await wencesApi.put(
            `/api/gotrading/ai/prompts/categories/${this.editingCategory._id}`,
            { name }
          );
        } else {
          await wencesApi.post("/api/gotrading/ai/prompts/categories", { name });
        }
        const wasEdit = !!this.editingCategory;
        await this.loadCategories();
        if (!this.promptForm.category && this.categories.length) {
          this.promptForm.category = this.categories[0].name;
        }
        this.cancelCategoryForm();
        Swal.fire({
          title: "Guardado",
          text: wasEdit ? "Categoría actualizada" : "Categoría creada",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        const msg =
          error.response?.data?.error || "Error al guardar la categoría";
        Swal.fire({ title: "Error", text: msg, icon: "error" });
      }
    },

    async deleteCategory(cat) {
      const result = await Swal.fire({
        title: "¿Eliminar categoría?",
        text: `Se eliminará «${cat.name}» si ningún prompt la usa.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) return;

      try {
        await wencesApi.delete(
          `/api/gotrading/ai/prompts/categories/${cat._id}`
        );
        if (this.promptForm.category === cat.name) {
          this.promptForm.category = "";
        }
        await this.loadCategories();
        if (this.categories.length && !this.promptForm.category) {
          this.promptForm.category = this.categories[0].name;
        }
        Swal.fire({
          title: "Eliminada",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        const msg =
          error.response?.data?.error || "Error al eliminar la categoría";
        Swal.fire({ title: "Error", text: msg, icon: "error" });
      }
    },

    async loadPrompts() {
      try {
        this.loading = true;
        const token = localStorage.getItem("token");
        const response = await wencesApi.get("/api/gotrading/ai/prompts", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          this.prompts = response.data.prompts;
        }
      } catch (error) {
        console.error("Error loading prompts:", error);
        Swal.fire({
          title: "Error",
          text: "Error al cargar los prompts",
          icon: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async loadAvailableModels() {
      try {
        console.log("Cargando modelos disponibles...");
        const token = localStorage.getItem("token");
        const response = await wencesApi.get(
          "/api/gotrading/model-definitions",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("Respuesta de modelos:", response.data);

        // La API devuelve directamente el array de modelos, no en un objeto success
        if (response.data && Array.isArray(response.data)) {
          this.availableModels = response.data;
          console.log("Modelos cargados:", this.availableModels.length);
        } else {
          console.error("Formato de respuesta inesperado:", response.data);
        }
      } catch (error) {
        console.error("Error loading available models:", error);
        Swal.fire({
          title: "Error",
          text: "Error al cargar los modelos disponibles",
          icon: "error",
        });
      }
    },

    // Cargar campos del modelo seleccionado
    async loadModelFields(modelName) {
      if (!modelName) {
        this.selectedModelFields = [];
        return;
      }

      try {
        const model = this.availableModels.find(
          (m) => m.modelName === modelName
        );
        if (model && model.fields) {
          this.selectedModelFields = model.fields.filter(
            (field) => !field.isHidden
          );
          console.log(
            "Campos del modelo cargados:",
            this.selectedModelFields.length
          );
        } else {
          this.selectedModelFields = [];
        }
      } catch (error) {
        console.error("Error loading model fields:", error);
        this.selectedModelFields = [];
      }
    },

    promptModelLabel(prompt) {
      const mn =
        prompt && prompt.modelName != null ? String(prompt.modelName).trim() : "";
      return mn || "Genérico";
    },

    editPrompt(prompt) {
      this.editingPrompt = prompt;
      this.promptForm = {
        ...prompt,
        modelName:
          prompt.modelName != null ? String(prompt.modelName).trim() : "",
      };
      this.showCreateModal = true;
    },

    async deletePrompt(prompt) {
      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: `¿Quieres eliminar el prompt "${prompt.name}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        try {
          await wencesApi.delete(`/api/gotrading/ai/prompts/${prompt._id}`);
          Swal.fire({
            title: "Eliminado",
            text: "El prompt ha sido eliminado exitosamente",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          await this.loadPrompts();
        } catch (error) {
          console.error("Error deleting prompt:", error);
          Swal.fire({
            title: "Error",
            text: "Error al eliminar el prompt",
            icon: "error",
          });
        }
      }
    },

    async savePrompt() {
      if (!this.canSavePrompt) {
        return;
      }

      try {
        this.loading = true;

        if (this.editingPrompt) {
          // Actualizar prompt existente
          await wencesApi.put(
            `/api/gotrading/ai/prompts/${this.editingPrompt._id}`,
            this.promptForm
          );
        } else {
          // Crear nuevo prompt
          await wencesApi.post("/api/gotrading/ai/prompts", this.promptForm);
        }

        Swal.fire({
          title: "¡Éxito!",
          text: this.editingPrompt
            ? "Prompt actualizado exitosamente"
            : "Prompt creado exitosamente",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        this.closeModal();
        await this.loadPrompts();
      } catch (error) {
        console.error("Error saving prompt:", error);
        Swal.fire({
          title: "Error",
          text: "Error al guardar el prompt",
          icon: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      console.log("Cerrando modal...");
      this.showCreateModal = false;
      this.editingPrompt = null;
      this.resetForm();
      console.log("Modal cerrado:", this.showCreateModal);
    },

    resetForm() {
      this.promptForm = {
        name: "",
        description: "",
        content: "",
        category: this.categories.length ? this.categories[0].name : "",
        modelName: "",
        isDefault: false,
      };
      this.selectedModelFields = [];
    },

    // Métodos para shortcodes
    onDragStart(event, field) {
      event.dataTransfer.setData(
        "text/plain",
        JSON.stringify({
          type: "field",
          key: field.key,
          label: field.label,
        })
      );
    },

    onDrop(event) {
      const data = JSON.parse(event.dataTransfer.getData("text/plain"));

      if (data.type === "field") {
        // Insertar el shortcode en la posición del cursor
        const textarea = event.target;
        const cursorPos = textarea.selectionStart;
        const shortcode = `[${data.key}]`;

        this.promptForm.content =
          this.promptForm.content.substring(0, cursorPos) +
          shortcode +
          this.promptForm.content.substring(cursorPos);
      }
    },

    insertField(field) {
      const textarea = document.getElementById("promptContent");
      if (textarea) {
        const cursorPos = textarea.selectionStart;
        const shortcode = `[${field.key}]`;

        this.promptForm.content =
          this.promptForm.content.substring(0, cursorPos) +
          shortcode +
          this.promptForm.content.substring(cursorPos);

        // Enfocar el textarea después de insertar
        this.$nextTick(() => {
          textarea.focus();
          textarea.setSelectionRange(
            cursorPos + shortcode.length,
            cursorPos + shortcode.length
          );
        });
      }
    },
  },
};
</script>

<style scoped>
.prompt-settings-container {
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 1rem;
}

.prompt-item {
  transition: all 0.3s ease;
}

.prompt-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.prompt-content {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  margin: 1rem 0;
}

.prompt-meta {
  margin-top: 1rem;
}

.prompt-category-group {
  margin-bottom: 1.5rem;
}

.prompt-category-group:last-child {
  margin-bottom: 0;
}

.prompt-category-heading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid #dee2e6;
}

.btn-group .btn {
  margin-left: 0.25rem;
}

.modal-backdrop {
  z-index: 1040;
}

.modal {
  z-index: 1050;
}

.form-control,
.form-select {
  border-radius: 0.375rem;
}

.card {
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

/* Estilos para el selector de campos */
.field-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}

.field-item {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.field-item:hover {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.field-item:active {
  transform: scale(0.95);
}

.field-item small {
  margin-left: 0.25rem;
  opacity: 0.7;
}

.field-group {
  background-color: #fff3cd !important;
  border-color: #ffeaa7 !important;
  color: #856404 !important;
}

.field-group:hover {
  background-color: #ffc107 !important;
  color: #000 !important;
  border-color: #ffc107 !important;
}

/* Estilos para el modal */
.modal.show {
  display: block !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
}

.modal-backdrop.show {
  display: block !important;
}

.modal-dialog {
  z-index: 1055 !important;
}

.modal-content {
  z-index: 1056 !important;
}

.modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}
</style>
